import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { container, SERVICE_IDENTIFIERS } from '@core/di/ServiceContainer';
import { INotificationService } from '@core/interfaces';

interface ErrorResponse {
    message?: string;
    properties?: string | string[];
    status?: number;
}

export const useNotifications = () => {
    const { t } = useTranslation();

    const notificationService = useMemo(
        () => container.get<INotificationService>(SERVICE_IDENTIFIERS.NOTIFICATION),
        []
    );

    const formatErrorMessage = (data?: { message?: string; properties?: string | string[] }) => {
        if (!data?.message) {
            return t('errors.unknownError');
        }

        let errMsg = t(`errors.${data.message}`);

        if (data.properties) {
            const propertiesArray = Array.isArray(data.properties) ? data.properties : [data.properties];

            const propertiesText = propertiesArray.join(', ');
            errMsg = errMsg.replace('{0}', propertiesText);
        }
        return errMsg;
    };

    const notifyErrorResponse = (error: any, title?: string) => {
        const typedError = error as ErrorResponse;
        const errMsg = formatErrorMessage(typedError ?? {});

        notificationService.error(title ? t(title) : t('error'), errMsg);
    };

    const notifyWarningResponse = (error: unknown, title?: string) => {
        const typedError = error as ErrorResponse;
        const errMsg = formatErrorMessage(typedError ?? {});

        notificationService.warning(title ? t(title) : t('warning'), errMsg);
    };

    const notifyError = (message: string, title?: string) => {
        notificationService.error(title ? t(title) : t('error'), t(message));
    };

    const notifyWarning = (message: string, title?: string) => {
        notificationService.warning(title ? t(title) : t('warning'), t(message));
    };

    const notifySuccess = (message?: string, title?: string) => {
        notificationService.success(title ? t(title) : t('successful'), message ? t(message) : undefined);
    };

    const notifyInfo = (message?: string, title?: string) => {
        notificationService.info(title ? t(title) : t('info'), message ? t(message) : undefined);
    };

    return {
        notifyErrorResponse,
        notifyWarningResponse,
        notifyError,
        notifyWarning,
        notifySuccess,
        notifyInfo
    };
};
